var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import '../styles/tooltip.scss';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import globalStore from '../store';
import { TooltipPortalContext } from './TooltipPortalContext';
var SHIFTING_CONSTANT = 10;
var ToolTip = function (_a) {
    var buttonHovered = _a.buttonHovered, _b = _a.direction, direction = _b === void 0 ? 'top' : _b, _c = _a.text, text = _c === void 0 ? '' : _c, _d = _a.toolTipCSS, toolTipCSS = _d === void 0 ? '' : _d, _e = _a.isParentRelative, isParentRelativeProp = _e === void 0 ? false : _e, _f = _a.isWhiteBoard, isWhiteBoard = _f === void 0 ? false : _f, content = _a.content, _g = _a.useViewportPosition, useViewportPositionProp = _g === void 0 ? false : _g;
    var _h = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _h.displayMode, zoomLevel = _h.zoomLevel, playerIconZoom = _h.playerIconZoom;
    // Context override: when an ancestor opts in (e.g. a scrollable drawer),
    // every tooltip inside it switches to viewport positioning + body portal so
    // it can escape the ancestor's overflow clip.
    var portalCtx = useContext(TooltipPortalContext);
    var useViewportPosition = useViewportPositionProp || portalCtx;
    var shouldPortal = portalCtx && !useViewportPositionProp ? true : false;
    var ref = useRef(null);
    // Stays in the original DOM tree (inside the button) even when the tooltip
    // body itself is portaled to document.body — gives us a reliable handle on
    // the button so positioning math still works post-portal.
    var anchorRef = useRef(null);
    var tipWidth = 6;
    var scale = 1;
    var _j = useState({ w: 0, h: 0 }), tooltipSize = _j[0], setSize = _j[1];
    var _k = useState({ x: 0, y: 0, w: 0, h: 0 }), parentPos = _k[0], setParentPos = _k[1];
    // Measure dimensions ONCE per hover
    useLayoutEffect(function () {
        var _a, _b;
        if (!ref.current)
            return;
        // When portaled, ref.current.parentElement is document.body — read the
        // real button via the anchor instead.
        var parent = shouldPortal
            ? (_b = (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) !== null && _b !== void 0 ? _b : null
            : ref.current.parentElement;
        if (!parent)
            return;
        // Tooltip dimension
        setSize({
            w: ref.current.offsetWidth / scale,
            h: ref.current.offsetHeight / scale,
        });
        if (useViewportPosition) {
            var rect = parent.getBoundingClientRect();
            setParentPos({
                x: rect.left / scale,
                y: rect.top / scale,
                w: rect.width / scale,
                h: rect.height / scale,
            });
        }
        else {
            // Position relative to parent — account for scroll offset in ancestor
            var scrollOffsetY = 0;
            var scrollOffsetX = 0;
            if (!isParentRelativeProp) {
                var el = parent;
                while (el && el !== document.body) {
                    if (el.scrollTop || el.scrollLeft) {
                        scrollOffsetY += el.scrollTop;
                        scrollOffsetX += el.scrollLeft;
                    }
                    el = el.parentElement;
                }
            }
            setParentPos({
                x: (parent.offsetLeft - scrollOffsetX) / scale,
                y: (parent.offsetTop - scrollOffsetY) / scale,
                w: parent.offsetWidth / scale,
                h: parent.offsetHeight / scale,
            });
        }
    }, [buttonHovered, direction, displayMode, zoomLevel, playerIconZoom]);
    // Re-measure when async content (e.g. MathJax) changes the tooltip's size after initial layout
    useEffect(function () {
        if (!ref.current || !buttonHovered)
            return;
        var el = ref.current;
        var observer = new ResizeObserver(function () {
            setSize({ w: el.offsetWidth / scale, h: el.offsetHeight / scale });
        });
        observer.observe(el);
        return function () { return observer.disconnect(); };
    }, [buttonHovered]);
    var getStyle = useMemo(function () {
        // if (!buttonHovered) return {};
        var style = {
        // transform: `scale(${playerIconZoom})`,
        };
        // Viewport-positioned tooltips are `position: fixed`, so offsets must be
        // in viewport coords — ignore the caller's isParentRelative hint.
        var isParentRelative = useViewportPosition ? false : isParentRelativeProp;
        switch (direction) {
            case 'left':
                style.left = isParentRelative
                    ? -tooltipSize.w - tipWidth
                    : parentPos.x - tooltipSize.w - tipWidth;
                style.top = isParentRelative
                    ? parentPos.h / 2 - tooltipSize.h / 2
                    : parentPos.y + parentPos.h / 2 - tooltipSize.h / 2;
                break;
            case 'leftTop':
                style.left = isParentRelative
                    ? -tooltipSize.w - tipWidth
                    : parentPos.x - tooltipSize.w - tipWidth;
                style.top = isParentRelative ? -SHIFTING_CONSTANT : parentPos.y - SHIFTING_CONSTANT;
                break;
            case 'leftBottom':
                style.left = isParentRelative
                    ? -tooltipSize.w - tipWidth
                    : parentPos.x - tooltipSize.w - tipWidth;
                style.top = isParentRelative
                    ? parentPos.h - tooltipSize.h + SHIFTING_CONSTANT
                    : parentPos.y + parentPos.h - tooltipSize.h + SHIFTING_CONSTANT;
                break;
            case 'right':
            case 'rightMiddle':
                style.left = isParentRelative
                    ? parentPos.w + tipWidth
                    : parentPos.x + parentPos.w + tipWidth;
                style.top = isParentRelative
                    ? parentPos.h / 2 - tooltipSize.h / 2
                    : parentPos.y + parentPos.h / 2 - tooltipSize.h / 2;
                break;
            case 'rightTop':
                style.left = isParentRelative
                    ? parentPos.w + tipWidth
                    : parentPos.x + parentPos.w + tipWidth;
                style.top = isParentRelative ? -SHIFTING_CONSTANT : parentPos.y - SHIFTING_CONSTANT;
                break;
            case 'rightBottom':
                style.left = isParentRelative
                    ? parentPos.w + tipWidth
                    : parentPos.x + parentPos.w + tipWidth;
                style.top = isParentRelative
                    ? parentPos.h - tooltipSize.h + SHIFTING_CONSTANT
                    : parentPos.y + parentPos.h - tooltipSize.h + SHIFTING_CONSTANT;
                break;
            case 'top':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tooltipSize.w / 2
                    : parentPos.x + parentPos.w / 2 - tooltipSize.w / 2;
                style.top = isParentRelative
                    ? -tooltipSize.h - tipWidth
                    : parentPos.y - tooltipSize.h - tipWidth;
                break;
            case 'topLeft':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tooltipSize.w + SHIFTING_CONSTANT
                    : parentPos.x + parentPos.w / 2 - tooltipSize.w + SHIFTING_CONSTANT;
                style.top = isParentRelative
                    ? -tooltipSize.h - tipWidth
                    : parentPos.y - tooltipSize.h - tipWidth;
                break;
            case 'topRight':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tipWidth - SHIFTING_CONSTANT
                    : parentPos.x + parentPos.w / 2 - tipWidth - SHIFTING_CONSTANT;
                style.top = isParentRelative
                    ? -tooltipSize.h - tipWidth
                    : parentPos.y - tooltipSize.h - tipWidth;
                break;
            case 'bottom':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tooltipSize.w / 2
                    : parentPos.x + parentPos.w / 2 - tooltipSize.w / 2;
                style.top = isParentRelative
                    ? parentPos.h + tipWidth
                    : parentPos.y + parentPos.h + tipWidth;
                break;
            case 'bottomLeft':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tooltipSize.w + SHIFTING_CONSTANT
                    : parentPos.x + parentPos.w / 2 - tooltipSize.w + SHIFTING_CONSTANT;
                style.top = isParentRelative
                    ? parentPos.h + tipWidth
                    : parentPos.y + parentPos.h + tipWidth;
                break;
            case 'bottomRight':
                style.left = isParentRelative
                    ? parentPos.w / 2 - tipWidth - SHIFTING_CONSTANT
                    : parentPos.x + parentPos.w / 2 - tipWidth - SHIFTING_CONSTANT;
                style.top = isParentRelative
                    ? parentPos.h + tipWidth
                    : parentPos.y + parentPos.h + tipWidth;
                break;
        }
        return style;
    }, [direction, isParentRelativeProp, useViewportPosition, parentPos, tooltipSize, playerIconZoom]);
    // Always render the anchor (a 0×0 element in the original DOM tree) so we
    // can read the real parent button via `anchorRef.current.parentElement`
    // even when the tooltip body itself is portaled to document.body. The
    // anchor is invisible to AT (`aria-hidden`) and takes no layout space.
    var anchor = (_jsx("span", { ref: anchorRef, "aria-hidden": "true", style: { display: 'none' } }));
    if (!buttonHovered) {
        return anchor;
    }
    var tipNode = (_jsx("div", { ref: ref, className: "tooltipContainer slide".concat(direction, " ").concat(buttonHovered ? 'showTip' : '', " ").concat(toolTipCSS), style: __assign(__assign(__assign({}, getStyle), { '--tipWidth': "".concat(tipWidth, "px"), '--shift-constant': "".concat(SHIFTING_CONSTANT, "px") }), (useViewportPosition ? { position: 'fixed' } : {})), children: _jsx("div", { className: "textHolder ".concat(isWhiteBoard ? '' : 'applyZoom'), children: content !== null && content !== void 0 ? content : t(text) }) }));
    return (_jsxs(_Fragment, { children: [anchor, shouldPortal ? createPortal(tipNode, document.body) : tipNode] }));
};
export default ToolTip;
